import React, { useState, useEffect, useRef, useCallback } from 'react';
import './ContentWindow.css';
import AiTile from './AiTile';
import config from './config';
import presentationsData from '../data/presentations';
import { registerProjectUpdater } from '../utils/externalControls';

// Component for handling project tiles
function ProjectTile({ project, currentSlideIndex, setCurrentSlideIndex }) {
    const tileRef = useRef();
    const [slides, setSlides] = useState(project.slides);

    // Function to update a specific field in a slide
    const updateSlideField = useCallback((slideIndex, field, newContent) => {
        if (slideIndex >= 0 && slideIndex < slides.length && field in slides[slideIndex]) {
            setSlides(prevSlides => {
                const newSlides = [...prevSlides];
                newSlides[slideIndex] = {
                    ...newSlides[slideIndex],
                    [field]: newContent
                };
                return newSlides;
            });
        }
    }, [slides]);

    // Register this project's update function with external controls
    useEffect(() => {
        registerProjectUpdater(project.id, updateSlideField);
    }, [project.id, updateSlideField]);

    const handleNextSlide = () => {
        if (currentSlideIndex < slides.length - 1) {
            const newIndex = currentSlideIndex + 1;
            setCurrentSlideIndex(newIndex);
        }
    };

    const handlePreviousSlide = () => {
        if (currentSlideIndex > 0) {
            const newIndex = currentSlideIndex - 1;
            setCurrentSlideIndex(newIndex);
        }
    };

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        // Slide is visible
                    }
                });
            },
            { threshold: 0.5 }
        );

        if (tileRef.current) {
            observer.observe(tileRef.current);
        }

        return () => {
            if (tileRef.current) {
                observer.unobserve(tileRef.current);
            }
        };
    }, [currentSlideIndex, project.id]);

    const renderSlideContent = () => {
        const slide = slides[currentSlideIndex];
        let temp_url = slide.slideInformation;

        switch (slide.slideType) {
            case 'text':
                return <p id={`slide-text-${project.id}-${currentSlideIndex}`}>{slide.slideInformation}</p>;
            case 'images':
                return <img src={`/media/images/${temp_url}`} alt={slide.slideDescription} className="slide-image media-content" />;
            case 'video':
                return <video controls src={`/media/videos/${temp_url}`} className="slide-video media-content">Your browser does not support the video tag.</video>;
            case 'pdf':
                return (
                    <div className="pdf-container">
                        <embed src={`${temp_url}`} type="application/pdf" className="slide-pdf media-content" />
                        <div className="pdf-overlay" onClick={() => window.open(`${temp_url}`, '_blank')}></div>
                    </div>
                );
            case 'embeddedLink':
                return (
                    <div className="iframe-container">
                        <iframe src={temp_url} title={slide.slideName} className="slide-embedded-link media-content"></iframe>
                        <div className="iframe-overlay" onClick={() => window.open(temp_url, '_blank')}></div>
                    </div>
                );
            default:
                return <p>Unsupported slide type</p>;
        }
    };

    return (
        <div className="project-tile-wrapper" ref={tileRef}>
            <div className="tile" id={project.id}>
                <button className="slide-nav-button left" onClick={(e) => { e.stopPropagation(); handlePreviousSlide(); }} disabled={currentSlideIndex === 0}>←</button>
                <div className="tile-content">
                    <h1 id={`slide-title-${project.id}-${currentSlideIndex}`}>{slides[currentSlideIndex].slideName}</h1>
                    <div className="slide-content">
                        {renderSlideContent()}
                    </div>
                    {slides[currentSlideIndex].slideDescription && (
                        <div className="slide-description">
                            <p id={`slide-description-${project.id}-${currentSlideIndex}`}>{slides[currentSlideIndex].slideDescription}</p>
                        </div>
                    )}
                </div>
                <button className="slide-nav-button right" onClick={(e) => { e.stopPropagation(); handleNextSlide(); }} disabled={currentSlideIndex === slides.length - 1}>→</button>
            </div>
        </div>
    );
}

function ContentWindow() {
    const [projects, setProjects] = useState([]);
    const [currentSlideIndices, setCurrentSlideIndices] = useState({});

    useEffect(() => {
        // Group the presentations data by presentation_id
        const projectsMap = {};

        presentationsData.forEach(item => {
            if (item.presentation_id && item.presentation_name) {
                if (!projectsMap[item.presentation_id]) {
                    projectsMap[item.presentation_id] = {
                        id: item.presentation_id,
                        title: item.presentation_name,
                        presentationId: item.presentation_id,
                        slides: []
                    };
                }

                projectsMap[item.presentation_id].slides.push({
                    slideId: item.slide_id,
                    slideName: item.slide_name,
                    slideType: item.slide_type,
                    slideInformation: item.slide_information,
                    slideDescription: item.slide_description
                });
            }
        });

        // Convert map to array and sort by presentation ID
        const fetchedProjects = Object.values(projectsMap);
        fetchedProjects.sort((a, b) => a.presentationId - b.presentationId);

        setProjects(fetchedProjects);

        // Initialize slide indices for each project
        const initialSlideIndices = fetchedProjects.reduce((acc, project) => {
            acc[project.id] = 0;
            return acc;
        }, {});

        setCurrentSlideIndices(initialSlideIndices);
    }, []);

    const setCurrentSlideIndex = (projectId, index) => {
        setCurrentSlideIndices(prevIndices => ({
            ...prevIndices,
            [projectId]: index,
        }));
    };

    return (
        <main className="content-window">
            <section className="ai-presentation">
                <AiTile />
            </section>
            <section className="project-tiles">
                {projects.map((project) => (
                    <ProjectTile
                        key={project.id}
                        project={project}
                        currentSlideIndex={currentSlideIndices[project.id] || 0}
                        setCurrentSlideIndex={(index) => setCurrentSlideIndex(project.id, index)}
                    />
                ))}
            </section>
        </main>
    );
}

export default ContentWindow;

// Comments for Logging:
// 1. 'navigateSlide': Logged when navigating between slides (either next or previous slide).
// 2. 'projectsRetrieved': Logged when projects are successfully retrieved from the backend.
// 3. 'fetchError': Logged when there's an error fetching presentations from the backend.
// 4. 'slideIndexUpdated': Logged when the current slide index for a project is updated.
// 5. 'slideViewed': Logged when a slide comes into the visible area (tracked
